import { zodResolver } from '@hookform/resolvers/zod';
import {
  accountNameSchema,
  MONEY_ACCOUNT_KINDS,
  moneyAccountKindSchema,
  sortCurrencies,
  UI_CURRENCIES,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { CurrencyLockNotice } from '../components/CurrencyLockNotice';
import { FormError } from '../components/FormError';
import { OpeningBalanceFields } from '../components/OpeningBalanceFields';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { latestRateMap, useCurrentRates } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey, useMembers } from '../lib/members';
import { currencyName } from '../lib/money';
import { checkOpening, openingFields, openingOutput } from '../lib/opening';

const ME = 'me';
const SHARED = 'shared';

/** The shared request schema, plus typed input ("12,5", "500k") parsed for the chosen currency. */
function formSchema(baseCurrency: string, latestRates: Record<string, string>) {
  return z
    .object({
      name: accountNameSchema,
      kind: moneyAccountKindSchema,
      owner: z.string().min(1),
      isPrivate: z.boolean(),
      ...openingFields,
    })
    .superRefine((v, ctx) => checkOpening(v, ctx, baseCurrency, latestRates))
    .transform((v) => ({ ...v, ...openingOutput(v, baseCurrency, latestRates) }));
}
type FormInput = z.input<ReturnType<typeof formSchema>>;
type FormOutput = z.output<ReturnType<typeof formSchema>>;

export function NewAccountPage() {
  const { t } = useTranslation();
  const me = useMe();
  // The form's defaults depend on my permissions, so wait for them.
  const rates = useCurrentRates();
  const latestRates = useMemo(() => latestRateMap(rates.data), [rates.data]);
  if (!me.data || rates.isPending) return <p role="status">{t('app.loading')}</p>;
  const family = me.data.family;
  return (
    <NewAccountForm
      myId={me.data.member.id}
      baseCurrency={family.baseCurrency}
      currencies={sortCurrencies(
        family.currencies.filter(
          (c) => c === family.baseCurrency || (UI_CURRENCIES as readonly string[]).includes(c),
        ),
        family.baseCurrency,
      )}
      latestRates={latestRates}
    />
  );
}

function NewAccountForm({
  myId,
  baseCurrency,
  currencies,
  latestRates,
}: {
  myId: string;
  baseCurrency: string;
  currencies: string[];
  latestRates: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const members = useMembers();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const canOwn = useCan('accounts.manage_own');
  const canShared = useCan('accounts.manage_shared');
  const canOthers = useCan('accounts.manage_others');
  const canPrivate = useCan('accounts.private');

  const schema = useMemo(() => formSchema(baseCurrency, latestRates), [baseCurrency, latestRates]);
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      kind: 'cash',
      owner: canOwn ? ME : SHARED,
      currency: baseCurrency,
      openingBalance: '',
      openingRate: '',
      isPrivate: false,
    },
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const owner = useWatch({ control: form.control, name: 'owner' });
  const currency = useWatch({ control: form.control, name: 'currency' });
  const openingBalance = useWatch({ control: form.control, name: 'openingBalance' });
  const openingRate = useWatch({ control: form.control, name: 'openingRate' });

  const create = useMutation({
    mutationFn: (v: FormOutput) =>
      api.accounts.create(
        {
          name: v.name,
          kind: v.kind,
          ownerMemberId: v.owner === ME ? myId : v.owner === SHARED ? null : v.owner,
          currency: v.currency,
          openingBalance: v.openingBalance,
          openingRate: v.openingRate,
          isPrivate: v.owner === ME && v.isPrivate,
        },
        { idempotencyKey },
      ),
    onSuccess: async (account) => {
      renewKey();
      await invalidate();
      navigate(`/accounts/${account.id}`, { replace: true });
    },
  });

  // Parents manage accounts of children and of members without a login (the API checks again).
  const others = (members.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (m) => m.id !== myId && m.status === 'active' && (m.role === 'child' || !m.hasLogin),
  );
  const errors = form.formState.errors;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('accountForm.newTitle')} back="/accounts" />
      <Card>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((v) => create.mutate(v))}
        >
          <FormError error={create.error} />
          <Field label={t('accountForm.name')} error={errors.name}>
            <Input placeholder={t('accountForm.namePlaceholder')} {...form.register('name')} />
          </Field>
          <Field
            label={t('accountForm.kind')}
            hint={
              i18n.exists(`accountKindHints.${kind}`) ? t(`accountKindHints.${kind}`) : undefined
            }
          >
            <Select {...form.register('kind')}>
              {MONEY_ACCOUNT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`accountKinds.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('accountForm.owner')}>
            <Select {...form.register('owner')}>
              {canOwn && <option value={ME}>{t('accountForm.ownerMe')}</option>}
              {canShared && <option value={SHARED}>{t('accountForm.ownerShared')}</option>}
              {canOthers &&
                others.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
            </Select>
          </Field>
          {currencies.length > 1 && (
            <Field label={t('accountForm.currency')} hint={t('accountForm.currencyHint')}>
              <Select {...form.register('currency')}>
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {t('currencies.option', { code: c, name: currencyName(c, i18n.language) })}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <OpeningBalanceFields
            label={
              kind === 'card'
                ? t('accountForm.openingBalanceCard')
                : t('accountForm.openingBalance')
            }
            currency={currency}
            baseCurrency={baseCurrency}
            latestRates={latestRates}
            amountValue={openingBalance}
            rateValue={openingRate}
            amountField={form.register('openingBalance')}
            rateField={form.register('openingRate')}
            amountError={errors.openingBalance}
            rateError={errors.openingRate}
          />
          {canPrivate && owner === ME && (
            <label className="flex min-h-11 items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 size-5 accent-accent"
                {...form.register('isPrivate')}
              />
              <span className="flex flex-col">
                <span className="font-medium">{t('accountForm.private')}</span>
                <span className="text-sm text-muted">{t('accountForm.privateHint')}</span>
              </span>
            </label>
          )}
          <CurrencyLockNotice />
          <Button type="submit" disabled={create.isPending}>
            {t('accountForm.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
